/**
 * Clinical Summary: the patient card's AI summary — its build act, version
 * strip, Word download and the clinic letterhead it is laid out on.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const clinicalSummary = {
  "patient.summary.title": "Clinical summary",
  "patient.summary.description": "Generated by AI from clinical chart data and patient documents.",
  "patient.summary.build": "Build clinical summary",
  "patient.summary.update": "Update summary",
  "patient.summary.full": "Build again",
  "patient.summary.history": "History",
  "patient.summary.language": "Summary language",
  "patient.summary.cancel": "Cancel generation",
  "patient.summary.empty": "No clinical summary has been generated yet.",
  "patient.summary.busy": "Source fragments processed: {done} of {total}",
  "patient.summary.failed": "Generation failed. The previous summary is preserved.",
  "patient.summary.cancelled": "Generation cancelled. The previous summary is preserved.",
  "patient.summary.showEmpty": "Show empty sections ({count})",
  "patient.summary.sources": "Sources",
  "patient.summary.modeFull": "Built from scratch",
  "patient.summary.modeUpdate": "Updated with new data",
  "patient.summary.modeCoverage": "Source list updated",
  "patient.summary.modeUnchanged": "No changes",
  "patient.summary.more": "Load earlier revisions",
  "patient.summary.readDenied": "You do not have access to all clinical summary sources.",
  "patient.summary.requestFailed": "The action could not be completed. Try again.",
  "patient.summary.warnings": "AI comments on summary preparation",
  "patient.summary.menu": "Clinical summary actions",
  "patient.summary.source": "Open",
  "patient.summary.noSources": "No usable clinical data or document text are available.",
  "patient.summary.unknownModel": "The selected model has no verified capability profile. Choose a supported model in AI settings.",
  "patient.summary.limit": "Generation reached a processing limit. The previous summary is preserved.",
  "patient.summary.latestLanguages": "Latest revisions by language",
  "patient.summary.languageResourcesInvalid": "Summary language resources are missing or invalid. Ask an administrator to check AI support files.",
  "patient.summary.busyTitle": "Generating the clinical summary…",
  "patient.summary.emptyDetail": "Review the sources, then start generation: AI assembles the summary from the selected chart data and documents, and every revision keeps its sources.",
  "patient.summary.stateLatest": "Current revision",
  "patient.summary.stateHistorical": "Historical revision",
  "patient.summary.backToLatest": "Back to current revision",
  "patient.summary.changedRetry": "The sources or choices changed while generation was starting. Load the current composition, review it and try again.",
  "patient.summary.aiProjectUnavailable": "This organization has no AI project set up yet. Ask an administrator to finish the organization's AI setup.",
  "patient.summary.keyMissing": "Add your OpenAI key in account settings before generating a summary.",
  "patient.summary.functionDisabled": "Clinical summary generation is disabled for this clinic in Control Center.",
  "patient.summary.serviceUnavailable": "The AI service is unavailable right now. Try again later.",
  "patient.summary.generateDenied": "You are not allowed to start summary generation.",
  "patient.summary.download": "Download Word",
  "patient.summary.downloadFailed": "The Word document could not be prepared. Try again.",
  "patient.summary.downloadLetterhead": "The clinic letterhead cannot be read. Replace or remove it in the summary menu.",
  "patient.summary.downloadUnavailable": "The document service is unavailable right now. Try again later.",
  "patient.summary.letterhead": "Clinic letterhead",
  "patient.summary.letterheadLoaded": "Loaded: {name}. Used in the Word documents of all patients.",
  "patient.summary.letterheadNone": "Not loaded. Word documents use a standard header with the organization’s details.",
  "patient.summary.letterheadUpload": "Upload letterhead (DOCX)",
  "patient.summary.letterheadReplace": "Replace letterhead",
  "patient.summary.letterheadRemove": "Remove letterhead",
  "patient.summary.letterheadPicker": "Letterhead file",
  "patient.summary.letterheadRemoveTitle": "Remove the clinic letterhead?",
  "patient.summary.letterheadRemoveDescription": "The Word documents of all patients will use the standard header with the organization’s details.",
  "patient.summary.letterheadRemoveConfirm": "Remove",
  "patient.summary.letterheadKeep": "Keep",
  "patient.summary.letterheadFailed": "The letterhead could not be saved. Try again.",
  "patient.summary.letterheadInvalid": "The file is not a Word document (DOCX).",
  "patient.summary.letterheadActive": "The letterhead contains macros, embedded objects or external content. Save it as a plain DOCX and upload it again.",
  "patient.summary.letterheadTooLarge": "The letterhead file must not exceed 4 MB.",
  "patient.summary.letterheadChanged": "Someone else has just changed the letterhead. Its current state is shown; try again.",
  "patient.summary.letterheadQuota": "The organization’s storage is full; the letterhead was not saved.",
  "patient.summary.letterheadDenied": "You are not allowed to change the clinic letterhead.",
  "patient.summary.compose": "Choose sources",
  "patient.summary.composeShared": "Choices apply to this patient’s summary for all clinicians and all summary languages. Unchecked sources are not sent to the AI.",
  "patient.summary.saveChoices": "Save choices",
  "patient.summary.saveAndBuild": "Save and build",
  "patient.summary.saveAndUpdate": "Save and update",
  "patient.summary.starting": "Starting…",
  "patient.summary.choicesSaved": "Choices saved. No generation was started.",
  "patient.summary.notStarted": "Generation did not start; the saved choices are kept.",
  "patient.summary.composeWhileRunning": "Generation is in progress. Choices can be saved now and apply to the next build.",
  "patient.summary.excludedChanged": "Changed since it was excluded",
  "patient.summary.reviewNeeded": "Excluded sources have changed",
  "patient.summary.reviewNeededDetail": "Review the source choices. Saving them confirms the exclusions as shown; it does not start generation.",
  "patient.summary.reviewSources": "Review sources",
  "patient.summary.choicesChanged": "The choices or sources have changed since this list was shown. Load the current composition and review it; your unsaved changes stay until then.",
  "patient.summary.loadCurrent": "Load current composition",
  "patient.summary.reloaded": "The current composition is shown; review it again.",
  "patient.summary.saveChoicesDenied": "You are not allowed to change the source choices.",
  "patient.summary.unsavedChoices": "Unsaved source choices for the clinical summary",
  "patient.summary.sourcesCount": "Sources · {count}",
  "patient.summary.untitled": "Untitled",
  "patient.summary.kindChart": "Title sheet and anamnesis",
  "patient.summary.kindRecords": "EMR records",
  "patient.summary.kindDocuments": "Documents",
  "patient.summary.kindLaboratory": "Laboratory results",
  "patient.summary.kindVitals": "Vital parameters",
  "patient.summary.kindReferrals": "Referrals",
  "patient.summary.kindStays": "Hospital stays",
  "patient.summary.kindOther": "Other",
  "patient.summary.kindCount": "{kind}: {count}",
  "patient.summary.measurement": "Measurement",
  "patient.summary.measurementValues": "values: {count}",
  "patient.summary.reasonGone": "No longer available",
  "patient.summary.composeTotal": "Will be used: {included} of {total}",
  "patient.summary.groupCount": "{included} of {total}",
  "patient.summary.groupTick": "Use: {group}",
  "patient.summary.versionUsed": "Sources used: {count}",
  "patient.summary.versionContents": "Contents",
  "patient.summary.period": "Data from {from} to {to}",
  "patient.summary.periodDay": "Data for {date}",
  "patient.summary.changedTitle": "The chart has changed since this revision was built",
  "patient.summary.changedAdded": "new sources: {count}",
  "patient.summary.changedEdited": "changed: {count}",
  "patient.summary.changedRemoved": "no longer used: {count}",
  "patient.summary.reviewNeededInline": "Excluded sources have changed; review the choices when updating.",
} as const;
